// Ported from v3 `composables/useCreditHistory.ts`: offset pagination, `PAGE_SIZE=50`, silent on
// failure. Two deliberate changes:
//   · `hasMore` is a COMPUTED, not a function — v3 returned `() => …`, so every template read it as
//     `hasMore()` and a forgotten call silently rendered a truthy function (ORP-18684 AC).
//   · account context comes from 07's public `useAccount()`, not `@/stores/accountStore`.
import { computed, ref, watch, type ComputedRef, type Ref } from 'vue'

import type { HistoryEntry } from '../models/credit.types'
import { useCreditStore } from '../stores/creditStore'

import { useAccount } from '@/features/account'

const PAGE_SIZE = 50

export interface UseCreditHistory {
  entries: Ref<HistoryEntry[]>
  loading: Ref<boolean>
  loadingMore: Ref<boolean>
  unavailable: Ref<boolean>
  total: Ref<number>
  hasMore: ComputedRef<boolean>
  fetchHistory: (offset?: number) => Promise<void>
  loadMore: () => Promise<void>
}

export function useCreditHistory(): UseCreditHistory {
  const store = useCreditStore()
  const { accountId } = useAccount()

  const entries = ref<HistoryEntry[]>([])
  const loading = ref(false)
  const loadingMore = ref(false)
  const unavailable = ref(false)
  const total = ref(0)
  let requestGeneration = 0

  const hasMore = computed(() => total.value > entries.value.length)

  async function fetchHistory(offset = 0): Promise<void> {
    if (accountId.value === undefined) return
    const generation = requestGeneration
    if (offset === 0) {
      loading.value = true
      unavailable.value = false
    } else loadingMore.value = true
    const payload = await store.fetchHistoryPage({ limit: PAGE_SIZE, offset })
    if (generation !== requestGeneration) return
    if (payload) {
      total.value = payload.count
      entries.value = offset === 0 ? payload.entries : [...entries.value, ...payload.entries]
    } else if (offset === 0 && entries.value.length === 0) {
      unavailable.value = true
    }
    loading.value = false
    loadingMore.value = false
  }

  function loadMore(): Promise<void> {
    if (loading.value || loadingMore.value || !hasMore.value) return Promise.resolve()
    return fetchHistory(entries.value.length)
  }

  watch(accountId, (next, previous) => {
    if (next === previous) return
    requestGeneration += 1
    entries.value = []
    total.value = 0
    loading.value = false
    loadingMore.value = false
    unavailable.value = false
    if (next !== undefined) void fetchHistory()
  })

  return { entries, loading, loadingMore, unavailable, total, hasMore, fetchHistory, loadMore }
}
